// dev_lp — footer + contact. Same content as the original footer.
// External contact links (mailto / WhatsApp) are functional content.
// The internal "terms" link is INERT (data-devlp-route) like the menu navigation.
import React from "react";
import { useTranslation } from "react-i18next";
import { deadLinkHandler } from "../menuConfig";

const MailIcon: React.FC<{ className?: string }> = ({ className = "" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const WhatsAppIcon: React.FC<{ className?: string }> = ({ className = "" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const FooterSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <footer
      id="contact-section"
      className="bg-white/80 backdrop-blur-sm border-t border-blue-100 py-14"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
        <h2
          className="text-2xl font-extrabold mb-5 bg-gradient-to-l from-blue-700 to-sky-500 bg-clip-text text-transparent inline-block"
          dir="ltr"
        >
          CarTax
        </h2>

        <p className="text-gray-700 mb-2">{t("footer.copyright")}</p>
        <p className="text-gray-600 mb-5">{t("footer.systemDesc")}</p>
        <p className="text-gray-400 text-sm mb-7">{t("footer.updated")}</p>

        {/* contact methods (functional content) */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-5 mb-6">
          <a
            href="mailto:info@cartax.org"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <MailIcon className="w-5 h-5" />
            <span className="font-medium">info@cartax.org</span>
          </a>

          <a
            href="https://wa.me/972544512136?text=%D7%A9%D7%9C%D7%95%D7%9D%20%D7%A8%D7%91%2C%20%D7%90%D7%A9%D7%9E%D7%97%20%D7%91%D7%9E%D7%99%D7%93%D7%A2%20%D7%A0%D7%95%D7%A1%D7%A3%20%D7%91%D7%A7%D7%A9%D7%A8%20%D7%9C%D7%9E%D7%A2%D7%A8%D7%9B%D7%AA%20Cartax"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-green-600 hover:text-green-800 transition-colors"
          >
            <WhatsAppIcon className="w-5 h-5" />
            <span className="font-medium" dir="ltr">
              +972 54-451-2136
            </span>
          </a>
        </div>

        {/* internal terms link — inert until wired */}
        <div className="mb-7">
          <a
            href="/terms-and-privacy"
            data-devlp-route="/terms-and-privacy"
            onClick={deadLinkHandler}
            className="text-sm text-blue-600 hover:text-blue-800 underline transition-colors"
          >
            {t("footer.termsLink")}
          </a>
        </div>

        {/* Eldor Systems credit */}
        <div className="pt-5 border-t border-gray-200 max-w-md mx-auto">
          <p className="text-xs text-gray-500 mb-2.5">{t("footer.builtBy")}</p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="mailto:eldor4ziv@gmail.com"
              className="text-gray-400 hover:text-blue-600 transition-colors"
              title={t("footer.emailTitle")}
            >
              <MailIcon className="w-4 h-4" />
            </a>
            <a
              href="https://wa.me/972546422629"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-green-600 transition-colors"
              title={t("footer.whatsappTitle")}
            >
              <WhatsAppIcon className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;
